/**
 * Store copy of the sticky buy button component. Loaded in place of the base
 * `sticky-buy-button.js` via a one-line hook in `snippets/sticky-buy-button.liquid`,
 * so the base asset stays pristine across base syncs. Keeps the `sticky-buy-button`
 * element name so existing CSS / kit-drawer / cart-notification selectors keep working.
 *
 * Differs from base in two ways:
 *  1. Header offset — accounts for sticky header + announcement bar height (CSS vars
 *     set by apc-header.js) so the bar isn't triggered while the form is behind the header.
 *  2. Only reveals the bar once the main add-to-cart button has been scrolled past (its
 *     bottom is above the header line), not while it's still below the fold. On mobile the
 *     image gallery pushes the main button below the fold, so the base "show whenever the
 *     button is out of view" logic covered the product image on load.
 *
 * When updating from base, re-apply both on top of the new base logic and keep the element name.
 */
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

customElements.define(
  'sticky-buy-button',
  class StickyBuyButton extends HTMLElement {
    connectedCallback() {
      // Move to body to escape stacking context
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return; // connectedCallback will be called again after moving
      }

      this.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement | null} */ (e.target);
        const anchor = target?.closest('a[href^="#"]');

        if (anchor) {
          e.preventDefault();
          const href = anchor.getAttribute('href');

          if (href) {
            document.querySelector(href)?.scrollIntoView({
              behavior: 'smooth',
            });
          }
        }
      });

      // Listen for variant updates
      document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

      const targetId = this.dataset.targetId;

      if (targetId) {
        const targetElement = document.getElementById(targetId);
        const footerElements = [...document.querySelectorAll('.shopify-section-group-footer-group')];

        // Height of the sticky header stack (header + announcement bar). Set by apc-header.js.
        const headerOffset = () => {
          const rootStyles = getComputedStyle(document.documentElement);
          return (
            parseInt(rootStyles.getPropertyValue('--header-height') || '0') +
            parseInt(rootStyles.getPropertyValue('--announcement-bar-height') || '0')
          );
        };

        let scrolledPastTarget = false; // add-to-cart button has scrolled up behind the header
        let footerVisible = false; // a footer-group section has started to appear on screen

        const update = () => this.toggleAttribute('data-visible', scrolledPastTarget && !footerVisible);

        // Track the actual add-to-cart button, not the whole buy-buttons form (which also holds the
        // quantity row + dynamic-checkout button, ~110px taller). Keying off the form made the bar
        // wait until that entire block cleared the header — i.e. well after the button itself had
        // gone behind it. Falls back to the form when the button can't be found.
        const triggerElement =
          targetElement?.querySelector('.add-to-cart-block, .add-to-cart-button, [name="add"]') || targetElement;

        // Reveal the bar the moment the add-to-cart button scrolls up behind the header — not while
        // it is still below the fold. The negative top rootMargin drops the trigger line to the
        // header's bottom edge, so the button counts as "past" exactly when it disappears behind the
        // header (base showed the bar whenever the button was out of view — including before it was
        // reached — which is what covered the product image on mobile).
        if (triggerElement) {
          const targetObserver = new IntersectionObserver(
            ([entry]) => {
              const lineTop = entry.rootBounds ? entry.rootBounds.top : headerOffset();
              scrolledPastTarget = !entry.isIntersecting && entry.boundingClientRect.bottom <= lineTop;
              update();
            },
            { rootMargin: `-${headerOffset()}px 0px 0px 0px`, threshold: 0 },
          );
          targetObserver.observe(triggerElement);
        }

        // Hide the bar as soon as the footer group starts to appear on screen (matches the live
        // theme's behavior). Any footer-group section entering the viewport counts.
        if (footerElements.length) {
          /** @type {Map<Element, boolean>} */
          const footerStates = new Map();
          const footerObserver = new IntersectionObserver(
            (entries) => {
              for (const entry of entries) footerStates.set(entry.target, entry.isIntersecting);
              footerVisible = [...footerStates.values()].some((value) => value);
              update();
            },
            { threshold: 0 },
          );
          footerElements.forEach((el) => footerObserver.observe(el));
        }

        update();
      }
    }

    disconnectedCallback() {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }

    /**
     * Handles variant update events to sync with new variant
     * @param {import('@theme/events').VariantUpdateEvent} event
     */
    #onVariantUpdate = (event) => {
      const { html } = event.detail.data;

      // Match by target id so a foreign sticky button (another product context in the fetched page)
      // isn't morphed in. Fall back to the first one when no target id is set.
      const targetId = this.dataset.targetId;
      const newStickyButton =
        (targetId && html.querySelector(`sticky-buy-button[data-target-id="${CSS.escape(targetId)}"]`)) ||
        html.querySelector('sticky-buy-button');
      if (newStickyButton) {
        morph(this, newStickyButton);
      }
    };
  },
);
